/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { House } from 'lucide-react';
import { useMemo, useState } from 'react';

/* Import context functions */
import { useI18n } from '@/context/I18nContext';

/* Import configuration */
import { APP_ROUTES } from '@/config/appRoutes';
import { DEMO_CSV_SYSTEMS, IS_DEMO_USER } from '@/config/csvSensors';
import { OVERVIEW_DEVICE_SENSOR_CONFIG } from '@/config/deviceSensorConfig';

/* Import components */
import OverviewAggregatedCard from '@/components/tabs/overview/metrics/cards/OverviewAggregatedCard';
import SystemAnomalyIcons, { getSystemAnomalyLabel } from '@/components/tabs/overview/system/SystemAnomalyIcons';
import SystemInformationModal from '@/components/tabs/overview/system/SystemInformationModal';

/* Import libraries */
import { DeviceSensorCatalog } from '@/lib/sensors/catalog/deviceSensorCatalog';
import { getSystemInformation } from '@/lib/sensors/presentation/systemInformation';

/* Import types */
import type { OverviewSystemCardData } from '@/lib/overview/overviewSystemAggregation';

interface OverviewSystemCardProps {
    card: OverviewSystemCardData;
    showKpis: boolean;
}

function OverviewSystemCard({ card, showKpis }: OverviewSystemCardProps) {
    const { t } = useI18n();
    const [isSystemInformationModalOpen, setIsSystemInformationModalOpen] = useState(false);
    const deviceSensorCatalog = useMemo(() => {
        return new DeviceSensorCatalog(OVERVIEW_DEVICE_SENSOR_CONFIG);
    }, []);
    const systemInformation = useMemo(() => {
        return getSystemInformation(deviceSensorCatalog, card.system);
    }, [card.system, deviceSensorCatalog]);
    const description = t('Metrics from system {system}.', { system: card.system });
    const anomalyCount = card.statusCounts.danger + card.statusCounts.warning;
    const anomalyDetails = card.anomalyIssueCounts
        .map(({ anomaly, sensorCount }) => `${sensorCount} x ${getSystemAnomalyLabel(anomaly, t)}`)
        .join(', ');
    const sensorLabel = card.sensorCount === 1 ? 'sensor' : 'sensors';
    const summaryText = anomalyCount === 0
        ? t(`No anomalies found for {sensorCount} ${sensorLabel}.`, {
            sensorCount: card.sensorCount,
        })
        : t(`{anomalyCount} ${anomalyCount === 1 ? 'anomaly' : 'anomalies'} from {sensorCount} ${sensorLabel} found: {details}`, {
            anomalyCount,
            sensorCount: card.sensorCount,
            details: anomalyDetails,
        });
    const anomalyIcons = <SystemAnomalyIcons anomalySensorCounts={card.anomalySensorCounts} />;
    const demoSystemConfig = IS_DEMO_USER
        ? DEMO_CSV_SYSTEMS[card.system as keyof typeof DEMO_CSV_SYSTEMS]
        : undefined;
    const demoComment = demoSystemConfig?.comment ?? '';
    const systemCard = (
        <OverviewAggregatedCard
            ariaLabel={description}
            card={card}
            compact
            contentSized
            description={description}
            icon={House}
            showKpis={showKpis}
            summaryText={summaryText}
            title={card.name}
            to={`${APP_ROUTES.systems}/${encodeURIComponent(card.system)}`}
            headerAction={(
                <button
                        type="button"
                        className="btn btn-ghost btn-sm h-auto min-h-0 px-1 py-1"
                        aria-label={t('Open info')}
                        title={t('Open info')}
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            setIsSystemInformationModalOpen(true);
                        }}
                        onMouseDown={(event) => {
                            event.stopPropagation();
                        }}
                    >
                        {anomalyIcons}
                </button>
            )}
        />
    );

    return (
        <>
            {demoComment ? (
                <div className="tooltip tooltip-top block h-full w-full" data-tip={demoComment}>
                    {systemCard}
                </div>
            ) : systemCard}
            <SystemInformationModal
                information={systemInformation}
                isOpen={isSystemInformationModalOpen}
                onClose={() => {
                    setIsSystemInformationModalOpen(false);
                }}
                statusCalculations={card.statusCalculations}
            />
        </>
    );
}

export default OverviewSystemCard;
